import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { formatDateTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/notifications").then(({data}) => setItems(data))
      .catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) { toast.error("Failed"); }
  };

  const markAll = async () => {
    try {
      await api.post(`/notifications/read-all`);
      setItems((prev) => prev.map(n => ({ ...n, read: true })));
      toast.success("All marked as read");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <AppShell
      subtitle="§ INBOX"
      title="Notifications"
      actions={
        <Button variant="outline" onClick={markAll} className="rounded-md h-10 border-black/20" disabled={items.every(i=>i.read)} data-testid="mark-all-read-btn">
          <CheckCircle size={14} weight="bold" className="mr-2" /> Mark all read
        </Button>
      }
    >
      {loading ? (
        <div className="border border-black/10 p-10 text-center text-neutral-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-black/20 p-16 text-center">
          <Bell size={32} weight="duotone" className="mx-auto text-neutral-400 mb-4" />
          <div className="fx-mono-tag mb-2">NOTHING TO SEE</div>
          <p className="text-neutral-600 text-sm">You're all caught up — we'll notify you when a project status changes.</p>
        </div>
      ) : (
        <div className="border border-black/10 max-w-3xl">
          {items.map(n => (
            <div key={n.id} className={`p-5 border-b border-black/5 last:border-b-0 flex items-start gap-4 ${n.read ? "" : "bg-[#EEF3FF]"}`} data-testid={`notification-${n.id}`}>
              <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${n.read ? "bg-neutral-300" : "bg-[#0B4DCC]"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <div className="font-semibold text-sm tracking-tight">{n.title}</div>
                  <span className="fx-mono-tag" dir="ltr" style={{fontSize:'12px'}}>{formatDateTime(n.created_at)}</span>
                </div>
                <p className="text-sm text-neutral-700">{n.message}</p>
                <div className="mt-3 flex items-center gap-3">
                  {n.project_id && (
                    <Link to={`/dashboard/projects/${n.project_id}`} className="text-xs font-mono uppercase tracking-widest text-[#0B4DCC] hover:underline flex items-center gap-1" data-testid={`notif-view-${n.id}`}>
                      VIEW PROJECT <ArrowUpRight size={11} weight="bold" />
                    </Link>
                  )}
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-black" data-testid={`notif-mark-read-${n.id}`}>
                      MARK READ
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
